// Decision logic for the Lists views, kept free of the DOM so it can be tested
// under bun test.

// The API takes at most this many people per invite request, so a larger wave
// goes out in chunks of it.
export const WAVE_CHUNK_SIZE = 25;

// One page of contacts.
export const CONTACTS_PAGE_SIZE = 100;

// The filters the page opens with: everyone on any list, newest first. An empty
// string is "either".
export const DEFAULT_FILTERS = Object.freeze({
    list: '',
    confirmed: '',
    gm: '',
    invited: '',
    sourceSite: '',
    email: '',
    from: '',
    to: '',
    sort: 'newest',
});

// The private beta queue: confirmed early-access sign-ups not yet invited,
// oldest first — the order to invite in.
export const QUEUE_FILTERS = Object.freeze({
    ...DEFAULT_FILTERS,
    list: 'early-access',
    confirmed: 'true',
    invited: 'false',
    sort: 'oldest',
});

export const LISTS = [
    { value: 'early-access', label: 'Early access' },
    { value: 'newsletter', label: 'Newsletter' },
];

export const SOURCE_SITES = [
    { value: 'vtt', label: 'vtt.dicedriven.com' },
    { value: 'dicedriven', label: 'dicedriven.com' },
];

/**
 * @param {string} value a list slug
 * @returns {string}
 */
export function listLabel(value) {
    return LISTS.find((l) => l.value === value)?.label ?? value;
}

/**
 * @param {string} value a source site slug
 * @returns {string}
 */
export function sourceSiteLabel(value) {
    return SOURCE_SITES.find((s) => s.value === value)?.label ?? value;
}

/**
 * The filters with any that can't apply dropped: GM and invited are early
 * access only, and the API refuses them alongside the newsletter.
 * @param {typeof DEFAULT_FILTERS} filters
 * @returns {typeof DEFAULT_FILTERS}
 */
export function normalizeFilters(filters) {
    if (filters.list !== 'newsletter') return filters;
    return { ...filters, gm: '', invited: '' };
}

/**
 * The start of a day in local time, from a date input's YYYY-MM-DD.
 * @param {string} date
 * @param {number} [addDays]
 * @returns {Date}
 */
function localDay(date, addDays = 0) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day + addDays);
}

/**
 * The API path for one page of contacts. `from` and `to` are date inputs and
 * are both inclusive: `to` becomes the start of the following day, since the
 * API's createdBefore is exclusive.
 * @param {typeof DEFAULT_FILTERS} filters
 * @param {string} [cursor]
 * @returns {string}
 */
export function contactsPath(filters, cursor = '') {
    const params = new URLSearchParams();
    for (const name of ['list', 'confirmed', 'gm', 'invited', 'sourceSite']) {
        if (filters[name]) params.set(name, filters[name]);
    }
    const email = filters.email.trim();
    if (email) params.set('email', email);
    if (filters.from) params.set('createdAfter', localDay(filters.from).toISOString());
    if (filters.to) params.set('createdBefore', localDay(filters.to, 1).toISOString());
    params.set('sort', filters.sort || 'newest');
    params.set('limit', String(CONTACTS_PAGE_SIZE));
    if (cursor) params.set('cursor', cursor);
    return `/contacts?${params}`;
}

/**
 * The page for one person.
 * @param {string} contactId
 * @returns {string}
 */
export function contactPath(contactId) {
    return `/lists/${encodeURIComponent(contactId)}`;
}

/**
 * @param {{confirmedAt?: string|null}} entry
 * @returns {boolean} whether the entry is waiting on its confirmation link
 */
export function isPending(entry) {
    return !entry.confirmedAt;
}

/**
 * The contact's early-access entry when it can be invited — confirmed and not
 * yet invited — or null. An unconfirmed address may be a typo or someone else's.
 * @param {{lists: {'early-access'?: {confirmedAt?: string|null, invitedAt?: string|null}}}} contact
 * @returns {object|null}
 */
export function invitableEntry(contact) {
    const entry = contact.lists['early-access'];
    if (!entry || isPending(entry) || entry.invitedAt) return null;
    return entry;
}

/**
 * @template T
 * @param {T[]} items
 * @param {number} size
 * @returns {T[][]}
 */
export function chunk(items, size) {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

/**
 * Invites a wave of early-access entries in chunks, so no one request is too
 * long. A chunk whose request fails outright (the API is unreachable, or the
 * type isn't provisioned) reports everyone in it as failed with that error, and
 * the wave goes on — one failure never stops the rest.
 * @param {(path: string, body: object) => Promise<{results: Array<object>}>} post
 * @param {string} type an invitation type slug
 * @param {string[]} ids early-access entry ids
 * @param {(done: number, total: number) => void} [onProgress]
 * @returns {Promise<Array<{entryId: string, contactId?: string, email?: string, status: string, detail?: string}>>}
 */
export async function inviteWave(post, type, ids, onProgress = () => {}) {
    const results = [];
    for (const entryIds of chunk(ids, WAVE_CHUNK_SIZE)) {
        try {
            const body = await post('/lists/early-access/invitations', { type, entryIds });
            results.push(...body.results);
        } catch (error) {
            for (const entryId of entryIds) {
                results.push({ entryId, status: 'failed', detail: error.message });
            }
        }
        onProgress(results.length, ids.length);
    }
    return results;
}

/**
 * @param {Array<{status: string}>} results
 * @returns {{invited: Array<object>, failed: Array<object>}}
 */
export function summarizeWave(results) {
    return {
        invited: results.filter((r) => r.status === 'invited'),
        failed: results.filter((r) => r.status !== 'invited'),
    };
}
